import { useCallback, useEffect, useRef, useState } from 'react';

export const DASHBOARD_REQUEST_TIMEOUT_MS = 15000;

// Bound the entire operation, including response.json(). Abort also settles the
// wrapper when an underlying request ignores its signal or never resolves.
export function readWithDeadline(read, controller, label, timeoutMs = DASHBOARD_REQUEST_TIMEOUT_MS) {
  let timer;
  let onAbort;
  const deadline = new Promise((resolve, reject) => {
    onAbort = () => reject(Object.assign(new Error('Request cancelled'), { name: 'AbortError' }));
    controller.signal.addEventListener('abort', onAbort, { once: true });
    if (controller.signal.aborted) return onAbort();
    timer = setTimeout(() => {
      reject(Object.assign(new Error(`${label} timed out after ${timeoutMs / 1000} seconds`), { name: 'TimeoutError' }));
      controller.abort();
    }, timeoutMs);
  });
  const operation = Promise.resolve().then(() => {
    if (controller.signal.aborted) throw Object.assign(new Error('Request cancelled'), { name: 'AbortError' });
    return read(controller.signal);
  });
  return Promise.race([operation, deadline]).finally(() => {
    clearTimeout(timer);
    controller.signal.removeEventListener('abort', onAbort);
  });
}

export default function useDashboardRequests() {
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState({});
  const requests = useRef(new Map());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const active = requests.current;
    return () => {
      mounted.current = false;
      for (const controller of active.values()) controller.abort();
      active.clear();
    };
  }, []);

  const run = useCallback(async (key, label, read, onSuccess, onFailure) => {
    if (!mounted.current) return;
    requests.current.get(key)?.abort();
    const controller = new AbortController();
    requests.current.set(key, controller);
    const isCurrent = () => mounted.current && requests.current.get(key) === controller;
    setPending(current => ({ ...current, [key]: label }));
    try {
      const data = await readWithDeadline(read, controller, label);
      if (!isCurrent()) return;
      onSuccess(data);
      setErrors(current => { const next = { ...current }; delete next[key]; return next; });
    } catch (error) {
      if (!isCurrent() || error.name === 'AbortError') return;
      setErrors(current => ({ ...current, [key]: { label, message: error.message || 'Request failed' } }));
      onFailure?.();
    } finally {
      if (isCurrent()) {
        requests.current.delete(key);
        setPending(current => { const next = { ...current }; delete next[key]; return next; });
      }
    }
  }, []);

  return { run, errors, pending, mounted };
}
